import { useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider, useAuth } from '@shared/providers/AuthProvider';
import { QueryProvider } from '@shared/providers/QueryProvider';
import { ThemeProvider } from '@shared/providers/ThemeProvider';
import { DataverseOrgProvider, getStoredOrgId } from '@shared/providers';
import {
  Button,
  Card,
  Title1,
  Body1,
  Caption1,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { veracityColors, DaybreakMark } from '@shared/ui';
import { router } from './routes/router';

// The dev build (vite --mode development) defaults to the DEV org so the dev
// shell shows dev data out of the box; the prod build defaults to prod. The
// env switcher still lets either deployment cross over (subject to that App
// Service's CSP allowlist + the user's Dataverse roles there). Without this,
// both builds defaulted to 'prod' and the dev shell hit the prod Dataverse —
// which the dev BFF's CSP blocks.
const DEFAULT_ORG = import.meta.env.MODE === 'development' ? 'dev' : 'prod';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: veracityColors.firstLight,
    // Sunrise backdrop — same Daybreak motif as the launcher hero.
    backgroundImage: `radial-gradient(120% 120% at 85% 115%, ${veracityColors.veracityOrange}26 0%, ${veracityColors.daybreakAmber}18 30%, transparent 62%), linear-gradient(125deg, ${veracityColors.firstLight} 0%, ${veracityColors.gentleHaze} 100%)`,
    padding: tokens.spacingHorizontalL,
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: '24px',
    border: '1px solid #f0dcc4',
    boxShadow: tokens.shadow28,
  },
  mark: {
    marginBottom: tokens.spacingVerticalL,
  },
  eyebrow: {
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.veracityOrange,
    marginBottom: tokens.spacingVerticalXS,
  },
  title: {
    color: veracityColors.midnightCharcoal,
    fontSize: '40px',
    lineHeight: '44px',
  },
  titleAccent: {
    color: veracityColors.veracityOrange,
  },
  subtitle: {
    color: veracityColors.duskBlue,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  button: {
    width: '100%',
  },
  errorBar: {
    width: '100%',
    marginBottom: tokens.spacingVerticalL,
  },
  footer: {
    marginTop: tokens.spacingVerticalXXL,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
  },
  footerLogo: {
    width: '28px',
    height: 'auto',
    display: 'block',
    flexShrink: 0,
  },
  footerText: {
    color: veracityColors.duskBlue,
    fontSize: tokens.fontSizeBase300,
    textAlign: 'left',
  },
});

function LoginPage() {
  const styles = useStyles();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setError(null);
    try {
      await login();
    } catch (err) {
      console.error('Login failed:', err);
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <DaybreakMark height={52} className={styles.mark} />
        <Caption1 className={styles.eyebrow}>OneVeracity</Caption1>
        <Title1 className={styles.title}>
          Welcome to <span className={styles.titleAccent}>Vera</span>
        </Title1>
        <Body1 className={styles.subtitle}>
          Sign in with your organization account to continue
        </Body1>
        {error && (
          <MessageBar intent="error" className={styles.errorBar}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        <Button
          appearance="primary"
          size="large"
          className={styles.button}
          onClick={handleLogin}
        >
          Sign in with Microsoft
        </Button>
        <div className={styles.footer}>
          <img src="/daybreak-logo.svg" alt="Daybreak logo" className={styles.footerLogo} />
          <Body1 className={styles.footerText}>
            Enhance care. Reduce costs. Make it easy.
          </Body1>
        </div>
      </Card>
    </div>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: veracityColors.firstLight }}>
        <Spinner size="large" label="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <LoginPage />
      </ThemeProvider>
    );
  }

  return (
    // Scope the persisted cache by USER and org: org-wide Ops data must never
    // paint from another user's IndexedDB on a shared/kiosk browser. Only
    // reached when authenticated, so user.oid is present.
    <QueryProvider persistScope={`vera-shell:${user?.oid ?? 'anon'}:${getStoredOrgId('vera-shell', DEFAULT_ORG)}`}>
      <DataverseOrgProvider appId="vera-shell" defaultOrg={DEFAULT_ORG}>
        <ThemeProvider>
          <RouterProvider router={router} />
        </ThemeProvider>
      </DataverseOrgProvider>
    </QueryProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
